import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import {
  CommonFilterState,
  FilterValue,
  initialCommonFilterState,
} from "interfaces/FilterTypes";

// Helper function to dynamically generate column visibility state
export const generateColumnVisibilityState = (columns: string[]) => {
  return columns.reduce(
    (acc, column) => {
      acc[column] = true;
      return acc;
    },
    {} as Record<string, boolean>,
  );
};

interface AppState {
  columnsPersist: any;
  columnsOrder: Record<string, string[]>;
  columnsWidth: Record<string, Record<string, number>>;
  commonFilter: { [key: string]: FilterValue };
}

const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
};

const debouncedSetColumns = debounce((columns: any) => {
  try {
    localStorage.setItem("columns", JSON.stringify(columns));
  } catch (error) {
    console.error("Failed to persist columns", error);
  }
}, 300);

const debouncedSetColumnsWidth = debounce((columnsWidth: any) => {
  try {
    const serializedData = JSON.stringify(columnsWidth);

    localStorage.setItem("columns-width", serializedData);
  } catch (error) {
    console.error("Failed to persist column widths", error);
  }
}, 300);

// const getInitialColumns = () => {
//   try {
//     const storedColumns = localStorage.getItem("columns");
//     return storedColumns ? JSON.parse(storedColumns) : {};
//   } catch (error) {
//     console.error("Failed to parse stored columns", error);
//     return {};
//   }
// };

const initialState: AppState = {
  columnsPersist: {},
  columnsOrder: {},
  columnsWidth: {},
  commonFilter: initialCommonFilterState,
};

const appSlice = createSlice({
  name: "table",
  initialState,
  reducers: {
    COLUMNS_PERSIST: (state, action) => {
      state.columnsPersist = action.payload;
      debouncedSetColumns(action.payload);
    },
    initializeColumnWidths: (
      state,
      action: PayloadAction<{ name: string; widths: Record<string, number> }>,
    ) => {
      if (!state.columnsWidth[action.payload.name]) {
        state.columnsWidth[action.payload.name] = action.payload.widths;

        const plain = current(state.columnsWidth);

        // Immediately update localStorage with the full state
        try {
          localStorage.setItem("columns-width", JSON.stringify(plain));
        } catch (error) {
          console.error("Failed to persist column widths", error);
        }
        // Also keep the debounced update as a backup
        debouncedSetColumnsWidth(plain);
      }
    },
    updateColumnWidth: (
      state,
      action: PayloadAction<{
        name: string;
        field: string;
        width: number;
        doubleClick?: boolean;
      }>,
    ) => {
      const { name, field, width, doubleClick } = action.payload;

      // Ensure base structure exists
      if (!state.columnsWidth) {
        state.columnsWidth = {};
      }

      if (doubleClick) {
        // Safe delete
        if (state.columnsWidth[name]?.[field] !== undefined) {
          delete state.columnsWidth[name][field];
        }
      } else {
        if (!state.columnsWidth[name]) {
          state.columnsWidth[name] = {};
        }
        state.columnsWidth[name][field] = width;
      }

      // make a safe copy (not a proxy)
      const plain = current(state.columnsWidth);

      // Immediately update localStorage with the full state
      try {
        localStorage.setItem("columns-width", JSON.stringify(plain));
      } catch (error) {
        console.error("Failed to persist column widths", error);
      }

      // Also keep the debounced update as a backup
      debouncedSetColumnsWidth(plain);
    },
    initializeColumns: (
      state,
      action: PayloadAction<{ name: string; fields: string[] }>,
    ) => {
      if (!state.columnsOrder[action.payload.name]) {
        state.columnsOrder[action.payload.name] = action.payload.fields;
        localStorage.setItem(
          "columns-order",
          JSON.stringify(state.columnsOrder),
        );
      }
    },
    updateColumnOrder: (
      state,
      action: PayloadAction<{ name: string; fields: string[] }>,
    ) => {
      state.columnsOrder[action.payload.name] = action.payload.fields;
      localStorage.setItem("columns-order", JSON.stringify(state.columnsOrder));
    },
    resetColumns: (state, action: PayloadAction<string>) => {
      const key = action.payload;

      // Create new objects to ensure state changes are detected
      const newColumnsOrder = { ...state.columnsOrder };
      const newColumnsWidth = { ...state.columnsWidth };
      const newColumnsPersist = { ...state.columnsPersist };

      delete newColumnsOrder[key];
      delete newColumnsWidth[key];
      delete newColumnsPersist[key];

      // Update state with new objects
      state.columnsOrder = newColumnsOrder;
      state.columnsWidth = newColumnsWidth;
      state.columnsPersist = newColumnsPersist;

      // Immediately update localStorage for all changes
      try {
        localStorage.setItem("columns-order", JSON.stringify(newColumnsOrder));
        localStorage.setItem("columns-width", JSON.stringify(newColumnsWidth));
        localStorage.setItem("columns", JSON.stringify(newColumnsPersist));
      } catch (error) {
        console.error("Failed to reset columns", error);
      }
    },
    updateFilter: (state, action) => {
      const { name, filter } = action.payload;
      state.commonFilter[name] = {
        ...state.commonFilter[name],
        ...filter,
      };
    },
    updateAllTableFilters: (
      state,
      action: PayloadAction<Partial<CommonFilterState>>,
    ) => {
      state.commonFilter = {
        ...state.commonFilter,
        ...action.payload,
      };
    },
  },
});

export const {
  COLUMNS_PERSIST,
  initializeColumns,
  updateColumnOrder,
  initializeColumnWidths,
  updateColumnWidth,
  resetColumns,
  updateFilter,
  updateAllTableFilters,
} = appSlice.actions;

export default appSlice.reducer;
